import React from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Checkbox } from '../../ui/Field';
import { pillPrimary, pillSecondary } from '../../ui/pills';
import { moduleApi } from '../../../lib/api';

export type ModeEnvoi = 'invitation' | 'relance';

/**
 * Sélecteur d'envoi (maquette 18c, « ConvivesRelanceModal · sélecteur d'envoi · 440 ») : à qui, quel
 * message, un seul bouton qui dit le verbe et le nombre. Le message est le modèle de l'établissement
 * (« Invitation » ou « Relance convives »), lu tel quel — il se modifie depuis le bouton « Emails » de
 * la page, pas ici. Sert aussi bien à la première invitation qu'à la relance : même geste, même forme.
 *
 * « Prévenir aussi l'organisateur » (case de la maquette 18c) : quand le dossier a un accès organisateur
 * actif (Portail Organisateur, lot 10), l'organisateur reçoit le compte des silencieux — sans leurs noms.
 */
export function ConvivesEnvoiModal({ mode, nombre, total, evenement, sending, onConfirm, onClose, organisateur }: {
    mode: ModeEnvoi;
    /** Combien de convives recevront ce courriel. */
    nombre: number;
    /** Combien de convives sont invités au total (pour « 12 convives sur 108 invités »). */
    total: number;
    evenement: string;
    sending: boolean;
    onConfirm: () => Promise<void> | void;
    onClose: () => void;
    /** Présent seulement si un accès organisateur est actif sur ce dossier. */
    organisateur?: { nom: string; coche: boolean; onChange: (v: boolean) => void };
}) {
    const [modele, setModele] = React.useState<{ sujet: string; corps: string } | null>(null);
    React.useEffect(() => {
        let annule = false;
        moduleApi.getConviveEmailSettings()
            .then((s: any) => {
                if (annule || !s) return;
                setModele(mode === 'relance'
                    ? { sujet: s.reminder_subject || '', corps: s.reminder_body || '' }
                    : { sujet: s.invitation_subject || '', corps: s.invitation_body || '' });
            })
            .catch(() => { if (!annule) setModele({ sujet: '', corps: '' }); });
        return () => { annule = true; };
    }, [mode]);

    const relance = mode === 'relance';
    const titre = relance ? 'Relancer les convives sans réponse' : 'Envoyer les invitations';
    const verbe = relance ? `Relancer ${nombre} convive${nombre > 1 ? 's' : ''}` : `Envoyer ${nombre} invitation${nombre > 1 ? 's' : ''}`;

    return (
        <Modal open onClose={onClose} widthPx={440} title={titre}
            subtitle={relance ? `${nombre} convive${nombre > 1 ? 's' : ''} sur ${total} invité${total > 1 ? 's' : ''}` : `${nombre} convive${nombre > 1 ? 's' : ''} pas encore invité${nombre > 1 ? 's' : ''}`}
            footer={
                <>
                    <button type="button" onClick={onClose} className={pillSecondary()} disabled={sending}>Annuler</button>
                    <button type="button" onClick={onConfirm} className={pillPrimary()} disabled={sending || nombre === 0}>
                        {sending ? <Loader2 size={13} className="animate-spin" /> : null} {verbe}
                    </button>
                </>
            }>
            <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-[9px] bg-surface-soft border border-border-soft">
                    <span className="text-[12px] text-content-muted">Destinataire</span>
                    <span className="text-[12px] font-semibold text-content">{nombre} convive{nombre > 1 ? 's' : ''} {relance ? 'sans réponse' : 'à inviter'}</span>
                </div>
                <div>
                    <div className="field-label">Message</div>
                    {modele === null ? (
                        <div className="flex items-center gap-2 text-[12px] text-content-muted"><Loader2 size={12} className="animate-spin" /> Chargement du modèle…</div>
                    ) : (
                        <div className="px-3 py-[11px] rounded-[9px] bg-surface border border-border text-[12.5px] leading-[1.55] text-content-muted whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
                            {modele.sujet && <div className="font-semibold text-content mb-1.5">{modele.sujet}</div>}
                            {modele.corps || `Le modèle par défaut de l'application sera utilisé pour ${evenement}.`}
                        </div>
                    )}
                    <p className="mt-1.5 text-[12px] text-content-muted">
                        Modèle « {relance ? 'Relance convives' : 'Invitation'} » — modifiable depuis le bouton « Emails » de cette page. Les envois partent par lots de 25.
                    </p>
                </div>
                {organisateur && (
                    <div className="px-[13px] py-[11px] rounded-[10px] bg-surface-soft border border-border-soft">
                        <Checkbox checked={organisateur.coche} onChange={(e) => organisateur.onChange(e.target.checked)}
                            label="Prévenir aussi l'organisateur" sub={`${organisateur.nom} reçoit le compte des silencieux, sans leurs noms.`} />
                    </div>
                )}
            </div>
        </Modal>
    );
}
